import React, { useState, useEffect, useRef } from 'react';
import { playWord } from '../utils/audio';
import { API_BASE } from '../api';
import { useSwipe } from '../hooks/useSwipe';
import { STATIC } from '../staticMode';
import { lookupGloss, lookupMainGloss } from '../dict';
import { shortGloss, dictSaysMore } from '../utils/glossText';

const API = API_BASE;

// Language name to put in the 網路搜尋 query when no dictionary has the word.
// This listed six languages while the app shipped sixteen books, so the other
// ten searched the bare word plus "族語" — the one query least likely to find
// anything, offered exactly where the dictionaries had already failed.
//
// Deliberately NOT App.js's ABOUT_DIALECT_ORDER, which is a DISPLAY list: its
// names carry the disambiguating parentheticals a credits screen wants —
// 賽德克語（Tgdaya）, 布農語（郡群）— and pasting those into a search engine
// narrows it to nothing. These are the plain names a person would actually type.
const SEARCH_LANG = {
  atayal: '泰雅語', tgdaya: '賽德克語', truku: '太魯閣語', saisiyat: '賽夏語',
  thao: '邵語', tsou: '鄒語', bunun: '布農語', paiwan: '排灣語', rukai: '魯凱語',
  kanakanavu: '卡那卡那富語', saaroa: '拉阿魯哇語', amis: '阿美語',
  sakizaya: '撒奇萊雅語', kavalan: '噶瑪蘭語', puyuma: '卑南語', yami: '達悟語',
};

// Word popup: meaning + learning status + preview. Timing is edited on the
// waveform now, so the numeric nudge editor is gone.
export default function WordPopup({
  word, dialect, sentenceId, wordIdx, allWords, audioUrl, audioStart,
  status, onStatusChange, onClose, onJumpToReview,
  getGloss, setGloss, getTimes, unitTranslation,
}) {
  const [omniGloss, setOmniGloss]       = useState(null);
  const [bookGloss, setBookGloss]       = useState('');    // the e-dict's own entry, under 詞典
  const [editingGloss, setEditingGloss] = useState(false);
  const [glossVal, setGlossVal]         = useState('');
  const [noteVal, setNoteVal]           = useState('');
  const [allSenses, setAllSenses]       = useState(false);   // e-dict piles: see utils/glossText

  const ref      = useRef();
  const glossRef = useRef();
  const userGloss = getGloss(dialect, word.text);

  useEffect(() => {
    // Both branches are async now — the static one fetches its dialect's
    // dictionary from R2 on first tap (see src/dict.js). `live` keeps a slow
    // reply for the previous word from overwriting the current one: tap two
    // words in quick succession and the answers can land out of order.
    let live = true;
    setOmniGloss(null); setBookGloss(''); setEditingGloss(false); setAllSenses(false);   // the fold is per word
    if (STATIC) {                                            // no backend: fetch the dict, not a gloss
      lookupGloss(word.text, dialect).then(g => { if (live) setOmniGloss(g); });
      // Same dictionary object, already loaded by the call above — no second fetch.
      lookupMainGloss(word.text, dialect).then(g => { if (live) setBookGloss(g); });
      return () => { live = false; };
    }
    const dq = dialect ? `?dialect=${encodeURIComponent(dialect)}` : '';
    fetch(`${API}/dictionary/${encodeURIComponent(word.text)}${dq}`)
      .then(r => r.json()).then(d => { if (live) setOmniGloss(d.gloss || ''); })
      .catch(() => { if (live) setOmniGloss(''); });
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word.text, sentenceId, wordIdx, dialect]);

  useEffect(() => {
    if (editingGloss && glossRef.current) glossRef.current.focus();
  }, [editingGloss]);

  useEffect(() => {
    function onMouse(e) {
      // keep the popup open while editing the word's block on the waveform
      if (e.target.closest && e.target.closest('.wave')) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', onMouse);
    return () => document.removeEventListener('mousedown', onMouse);
  }, [onClose]);

  function playCurrentTimes() {
    if (!audioUrl) return;
    const t = getTimes ? getTimes(sentenceId, wordIdx, word) : word;
    playWord(audioUrl, (audioStart ?? 0) + t.start, (audioStart ?? 0) + t.end);
  }

  // Keyboard: ← → prev/next word, space preview, Esc close
  const playRef     = useRef(playCurrentTimes);
  const onCloseRef  = useRef(onClose);
  const allWordsRef = useRef(allWords);
  const wordIdxRef  = useRef(wordIdx);
  playRef.current     = playCurrentTimes;
  onCloseRef.current  = onClose;
  allWordsRef.current = allWords;
  wordIdxRef.current  = wordIdx;

  useEffect(() => {
    function onKey(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault(); e.stopPropagation();
        const p = allWordsRef.current[wordIdxRef.current - 1];
        if (p) onCloseRef.current(p, wordIdxRef.current - 1);
        else onCloseRef.current('__cross__', -1);     // first word → previous segment's last word
      } else if (e.key === 'ArrowRight') {
        e.preventDefault(); e.stopPropagation();
        const n = allWordsRef.current[wordIdxRef.current + 1];
        if (n) onCloseRef.current(n, wordIdxRef.current + 1);
        else onCloseRef.current('__cross__', 1);      // last word → next segment's first word
      } else if (e.key === ' ') {
        e.preventDefault(); e.stopPropagation();
        playRef.current();
      } else if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        onCloseRef.current();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function startEditGloss() {
    setGlossVal(userGloss ?? omniGloss ?? '');
    setNoteVal('');
    setEditingGloss(true);
  }
  // `was` is the dictionary's answer as it stood on screen — null while the
  // lookup is still in flight, '' when no dictionary had the word. useGloss
  // keeps only the first one, so this stays the published gloss even after a
  // reviewer edits their own edit.
  function saveGloss() {
    // The edit box is PREFILLED with the dictionary's answer, so opening it and
    // saving without typing files the whole aggregated entry as a personal
    // correction — green, exempt from the fold, and outliving every later fix
    // to the dictionary. That is not a correction, it is a no-op: clear it.
    const v = glossVal.trim();
    setGloss(dialect, word.text, v === (omniGloss || '').trim() ? '' : v,
             { was: omniGloss, note: noteVal.trim() });
    setEditingGloss(false);
  }

  const displayGloss = userGloss ?? omniGloss;
  // A user's own gloss is normally one meaning and folds to itself. It is folded
  // anyway rather than passed through: devices already carry glosses saved from
  // the prefill above, and those ARE aggregated entries — exempting them is what
  // let a seven-sense pile sit on yami 'ya' unfolded. Folding is display only;
  // the raw string stays behind glossVal/`was`, so nothing of theirs is lost.
  const folded = shortGloss(displayGloss);
  // A user gloss SHADOWS the dictionary — and silently, which is how yami 'ya'
  // sat on 他；它 for a week after its entry had been rewritten to the aspect
  // marker: the reader had no way to know a gloss was theirs rather than the
  // book's, or that the book now disagreed. Show what the dictionary currently
  // says behind the same tap the senses use. Emptying the edit box already
  // reverts to it; this is what makes that worth doing.
  const shadowed = (userGloss && omniGloss && omniGloss.trim() !== userGloss.trim())
    ? omniGloss : '';
  // One 詞典 section, whichever layer the reader is NOT being shown: a user
  // gloss hides the app's, and the curated *_extra overlay hides the book's
  // (that is what the overlay is FOR — see lookupMainGloss). Suppressed when it
  // adds no sense the expanded headline already shows, because most overlay
  // entries are a tidied version of the very entry they shadow, and printing
  // both would put 詞典 on nearly every word while telling the reader nothing.
  // The test is sense-by-sense (addedSenses), NOT a substring one: `folded.full`
  // is what splitSenses left of the entry, so any entry it actually cleaned up
  // is no longer a slice of the original and slipped straight past the old
  // check — see the comment on addedSenses.
  const book = shadowed || bookGloss;
  // Compare against `all`, not `full`: shortGloss now moves a particle's
  // syntax off the headline and onto 用法, so `full` no longer holds every
  // sense the entry does. Testing against it would report a difference the
  // reader is already looking at, and put 詞典 on every case marker.
  const dictNote = dictSaysMore(book, folded.all || folded.full) ? book : '';
  const hasMore = !!(folded.more || folded.notes.length || dictNote);
  // The label was a priority chain — first hit wins — so a word with two hidden
  // senses AND a 構詞 line advertised only the senses, and the same tap opened
  // different things on different words with no way to tell beforehand. List
  // every section instead, always in this order: the label IS the contents.
  const sections = [
    folded.more ? `還有 ${folded.more} 個義項` : '',
    ...folded.notes.map(n => n.label),
    dictNote ? '詞典' : '',
  ].filter(Boolean);
  const prevWord = allWords[wordIdx - 1];
  const nextWord = allWords[wordIdx + 1];

  // Swipe: left = next word, right = prev word (mirrors the ← → keys above).
  const swipe = useSwipe({
    onLeft:  () => { nextWord ? onClose(nextWord, wordIdx + 1) : onClose('__cross__', 1); },
    onRight: () => { prevWord ? onClose(prevWord, wordIdx - 1) : onClose('__cross__', -1); },
  });

  return (
    <div className="word-popup" ref={ref} {...swipe}>
      {/* 跳到複習 — above the word; learning words only (caller decides which views). */}
      {status === 'learning' && onJumpToReview && (
        <button tabIndex={-1} className="popup-jump-btn" onClick={() => onJumpToReview()}>跳到複習 ↗</button>
      )}
      <div className="popup-header">
        <span className="popup-word">{word.text}</span>
        <div className="popup-header-actions">
          {audioUrl && (
            <button tabIndex={-1} className="popup-play-btn" onClick={playCurrentTimes}>▶</button>
          )}
          <button tabIndex={-1} className="popup-close" onClick={() => onClose()}>×</button>
        </div>
      </div>

      <div className="popup-body">
        {omniGloss === null && !userGloss && <div className="popup-loading">...</div>}
        {/* Gloss editing is ON in the static build. It used to be gated off with
            the rest of editing, but the two are not the same thing: a TIMING
            edit rewrites the story file everyone downloads, while a gloss edit
            writes one key in this browser's own store (useGloss -> glossStore,
            backed up by cloudSync's BACKUP_KEYS). Nothing shared changes, so
            there was nothing for the gate to protect. Timings stay locked, at
            SentenceView's `locked={STATIC}` — that is the gate that matters. */}
        {editingGloss ? (
          <div className="popup-edit-box">
            <div className="popup-edit-row">
              <input ref={glossRef} className="popup-edit-input" value={glossVal}
                onChange={e => setGlossVal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveGloss(); if (e.key === 'Escape') setEditingGloss(false); }}
                placeholder="輸入中文意思" />
              <button tabIndex={-1} className="popup-save-btn" onClick={saveGloss}>儲存</button>
            </div>
            {/* Optional, and the most valuable field on this screen when a
                speaker is the one typing: a replacement gloss says what, and
                only this says why. */}
            <input className="popup-edit-note" value={noteVal}
              onChange={e => setNoteVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveGloss(); if (e.key === 'Escape') setEditingGloss(false); }}
              placeholder="備註（可不填）：為什麼這樣改？" />
          </div>
        ) : (
          <div className="popup-gloss-row" onClick={startEditGloss} title="點擊修改">
            {displayGloss
              ? <span className={`popup-gloss${userGloss ? ' user-edited' : ''}`}>
                  {allSenses ? folded.full : (hasMore ? folded.head : displayGloss)}
                  {/* Own line, own label. This used to be a bracket welded to
                      the end of the meaning, which read as a second meaning —
                      ma-＋ka-nem is not something 滿六年 also means. */}
                  {allSenses && folded.notes.map((n, i) => (
                    <span key={i} className="popup-gloss-note">
                      <b className="popup-note-label">{n.label}</b>{n.text}
                    </span>
                  ))}
                  {allSenses && dictNote && (
                    <span className="popup-gloss-note">
                      <b className="popup-note-label">詞典</b>{dictNote}
                    </span>
                  )}
                  {hasMore && (
                    <button tabIndex={-1} className="popup-more-senses"
                      onClick={e => { e.stopPropagation(); setAllSenses(v => !v); }}>
                      {allSenses ? '收合' : `⋯${sections.join('・')}`}
                    </button>
                  )}
                </span>
              : unitTranslation
                ? <span className="popup-gloss popup-gloss-ctx">句意：{unitTranslation}</span>
                : <span className="popup-no-def">（查無此詞）</span>}
            {/* Carried over from the old read-only branch, which is where this
                link used to live: when the dictionaries have nothing, an outside
                search is the only next move, and it was about to be deleted
                along with the gate. stopPropagation so it opens the search
                instead of the gloss editor underneath it. */}
            {!displayGloss && (
              <a className="popup-search-link" target="_blank" rel="noreferrer"
                onClick={e => e.stopPropagation()}
                href={`https://www.google.com/search?q=${encodeURIComponent(`"${(word.text || '').replace(/[^\w\-’'ʼ‘^ ]/g, '')}" ${SEARCH_LANG[dialect] || '族語'}`)}`}
              >🔍 網路搜尋</a>
            )}
            <span className="popup-edit-icon">✎ 修改{userGloss ? ' ·已修改' : ''}</span>
          </div>
        )}
      </div>

      <div className="popup-status">
        <button tabIndex={-1} className={`status-btn btn-new${status === 'new' ? ' active' : ''}`}
          onClick={() => onStatusChange('new')}>未學</button>
        <button tabIndex={-1} className={`status-btn btn-learning${status === 'learning' ? ' active' : ''}`}
          onClick={() => onStatusChange('learning')}>學習中</button>
        <button tabIndex={-1} className={`status-btn btn-known${status === 'known' ? ' active' : ''}`}
          onClick={() => onStatusChange('known')}>已知</button>
      </div>

      {/* Word timing is edited on the waveform (drag the cuts), not here. */}
    </div>
  );
}
